/**
 * Salary Ranges Tab
 *
 * Manage salary ranges per niveau.
 */

import { useState } from 'react';
import { useSalaryRanges, useUpdateSalaryRange } from '../useSettings';
import type { SalaryRangeDTO, UpdateSalaryRangeRequest } from '@functieloonhuis/shared';

interface EditingState {
  niveauId: number;
  minSalary: string;
  maxSalary: string;
  notes: string;
}

export function SalaryRangesTab() {
  const { data: salaryRanges, isLoading, error } = useSalaryRanges();
  const updateSalaryRange = useUpdateSalaryRange();

  const [editing, setEditing] = useState<EditingState | null>(null);

  const handleEdit = (range: SalaryRangeDTO) => {
    setEditing({
      niveauId: range.niveauId,
      minSalary: String(range.minSalary),
      maxSalary: String(range.maxSalary),
      notes: range.notes || '',
    });
  };

  const handleSave = async () => {
    if (!editing) return;

    const data: UpdateSalaryRangeRequest = {
      minSalary: Number(editing.minSalary),
      maxSalary: Number(editing.maxSalary),
      notes: editing.notes || undefined,
    };

    await updateSalaryRange.mutateAsync({ niveauId: editing.niveauId, data });
    setEditing(null);
  };

  const handleCancel = () => {
    setEditing(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van salarisschalen.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">Salarisschalen</h2>
        <p className="text-sm text-gray-500">
          Beheer minimale en maximale salarissen per functieniveau
        </p>
      </div>

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-700">
            Salarisschalen worden automatisch aangemaakt voor elk functieniveau. U kunt hier de minimum en maximum waarden aanpassen.
          </p>
        </div>
      </div>

      {/* Salary Ranges Table */}
      {salaryRanges && salaryRanges.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Niveau
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Minimum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Maximum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bereik
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notities
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {salaryRanges.map((range) => (
                <tr key={range.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {range.niveauName || `Niveau ${range.niveauId}`}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editing !== null && editing.niveauId === range.niveauId ? (
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-1">€</span>
                        <input
                          type="number"
                          value={editing.minSalary}
                          onChange={(e) => setEditing({ ...editing, minSalary: e.target.value })}
                          className="w-28 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          min={0}
                          step={100}
                        />
                      </div>
                    ) : (
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(range.minSalary)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editing !== null && editing.niveauId === range.niveauId ? (
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-1">€</span>
                        <input
                          type="number"
                          value={editing.maxSalary}
                          onChange={(e) => setEditing({ ...editing, maxSalary: e.target.value })}
                          className="w-28 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          min={0}
                          step={100}
                        />
                      </div>
                    ) : (
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(range.maxSalary)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 w-24 mr-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min(100, (range.maxSalary - range.minSalary) / range.maxSalary * 100)}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatCurrency(range.maxSalary - range.minSalary)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {editing !== null && editing.niveauId === range.niveauId ? (
                      <input
                        type="text"
                        value={editing.notes}
                        onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        placeholder="Optionele notities..."
                      />
                    ) : (
                      <span className="text-sm text-gray-500 truncate max-w-xs block">
                        {range.notes || '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {editing !== null && editing.niveauId === range.niveauId ? (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={handleCancel}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Annuleren
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={updateSalaryRange.isPending}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {updateSalaryRange.isPending ? 'Opslaan...' : 'Opslaan'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(range)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Bewerken
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Geen salarisschalen gevonden</h3>
          <p className="mt-2 text-sm text-gray-500">
            Salarisschalen worden automatisch aangemaakt wanneer functieniveaus worden toegevoegd.
          </p>
        </div>
      )}
    </div>
  );
}
